// src/components/Receipts/ReceiptsManagement.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  IconButton,
  Tab,
  Tabs,
  Typography,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  alpha,
  Chip,
  Stack,
  Fab,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  Drawer,
  List,
  ListItem,
  Snackbar,
  Skeleton,
  Tooltip,
  Paper,
  TextField,
  InputAdornment,
  Checkbox,
  Popover,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  Photo as PhotoIcon,
  Email as EmailIcon,
  MoreVert as MoreIcon,
  ReceiptLong as ReceiptIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  CameraAlt as CameraIcon,
  Search as SearchIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  UploadFile as UploadFileIcon,
  Refresh as RefreshIcon,
  ErrorOutline as ErrorOutlineIcon,
  Warning as WarningAmberIcon,
  Tune as TuneIcon
} from '@mui/icons-material';

import api from '../../services/api';
import LockedFeature from '../Subscription/LockedFeature';
import { useUpgradeGate } from '../../hooks/useUpgradeGate';
import ReceiptCapture from './ReceiptCapture';
import ReceiptProcessingScreen from './ReceiptProcessingScreen';
import ReceiptLinkSuggestions from './parts/ReceiptLinkSuggestions';
import ReceiptDetailDialog from './parts/ReceiptDetailDialog';
import ExpenseReportDialog from './parts/ExpenseReportDialog';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency, formatDate } from '../../utils/helpers';
import { useCurrency } from '../../contexts/CurrencyContext';
import { extractSoftPaywall, dispatchSoftPaywall, isSoftPaywallError } from '../../utils/softPaywall';

// Build image URL from api base
const getImageUrl = (receiptId) => {
  const base = (api.defaults && api.defaults.baseURL) || 'https://soldikeeper-backend-production.up.railway.app/api';
  return `${base}/receipts/${receiptId}/image`;
};

// Helper to format relative time
const formatRelativeTime = (date) => {
  const now = new Date();
  const diffMs = now - new Date(date);
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(date);
};

// F3: source label helper
const sourceLabel = (source) => {
  switch (source) {
    case 'scan':
    case 'camera': return 'Scanned';
    case 'email': return 'Email';
    case 'upload': return 'Upload';
    case 'manual': return 'Manual';
    default: return source || 'Unknown';
  }
};

// Unified Receipt Card Component
// F4: accepts selectMode, selected, onSelect props
// AI-1: accepts onLinkClick prop for orphan chip
const ReceiptCard = React.memo(({ receipt, onAction, t, selectMode, isSelected, onSelect, onLinkClick, onAnomalyClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [imgError, setImgError] = useState(false);

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };

  const handleMenuClose = () => setMenuAnchor(null);

  const handleAction = (action) => {
    handleMenuClose();
    onAction(action, receipt);
  };

  // F1: use getImageUrl helper
  const imageUrl = receipt.id ? getImageUrl(receipt.id) : null;

  const isScanned = receipt.source === 'scan' || receipt.source === 'camera';
  // F6: linked/orphan
  const isLinked = !!receipt.linkedTransactionId;

  return (
    <Card 
      onClick={selectMode ? () => onSelect(receipt.id || receipt._id) : undefined}
      sx={{ 
        mb: 1.5,
        transition: 'all 0.2s',
        outline: isSelected ? `2px solid` : 'none',
        outlineColor: isSelected ? 'primary.main' : 'transparent',
        cursor: selectMode ? 'pointer' : 'default',
        '&:hover': {
          boxShadow: theme.shadows[4],
          transform: 'translateY(-2px)'
        }
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2, md: 2 }, '&:last-child': { pb: { xs: 2, sm: 2, md: 2 } } }}>
        <Stack direction="row" spacing={2} alignItems="center">
          {/* F4: Checkbox in select mode, thumbnail otherwise */}
          {selectMode ? (
            <Checkbox
              checked={isSelected}
              onChange={() => onSelect(receipt.id || receipt._id)}
              onClick={(e) => e.stopPropagation()}
              sx={{ flexShrink: 0, p: 0 }}
            />
          ) : (
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              {imageUrl && !imgError ? (
                <img
                  src={imageUrl}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={() => setImgError(true)}
                />
              ) : (
                <ReceiptIcon color="primary" fontSize="small" />
              )}
            </Box>
          )}

          {/* Receipt Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              {/* 6b: shimmer when processing */}
              {receipt.merchantName === 'Processing...' || receipt.status === 'processing' ? (
                <Skeleton variant="text" width="60%" height={20} sx={{ flex: 1 }} />
              ) : (
                <Tooltip title={receipt.merchantName || ''} enterDelay={500}>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    noWrap
                    sx={{ flex: 1 }}
                  >
                    {receipt.merchantName || t('receipts.unknownMerchant', 'Unknown Merchant')}
                  </Typography>
                </Tooltip>
              )}
              {/* F3: source chip — informational (outlined + dimmed) */}
              <Chip
                size="small"
                icon={isScanned ? <PhotoIcon /> : <EmailIcon />}
                label={sourceLabel(receipt.source)}
                variant="outlined"
                sx={{
                  height: 20,
                  opacity: 0.7,
                  cursor: 'default',
                  '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' },
                  '& .MuiChip-icon': { fontSize: '0.875rem', ml: 0.5 }
                }}
              />
              {/* F6: Linked / Orphan chip */}
              {isLinked ? (
                <Chip
                  size="small"
                  icon={<LinkIcon />}
                  label={t('receipts.linked', 'Linked')}
                  color="success"
                  variant="outlined"
                  sx={{
                    height: 20,
                    opacity: 0.75,
                    cursor: 'default',
                    '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' },
                    '& .MuiChip-icon': { fontSize: '0.875rem', ml: 0.5 }
                  }}
                />
              ) : (
                /* Actionable: filled + shadow + pointer */
                <Chip
                  size="small"
                  icon={<LinkIcon />}
                  label={t('receipts.orphan', 'Orphan')}
                  color="warning"
                  variant="filled"
                  onClick={(e) => { e.stopPropagation(); onLinkClick && onLinkClick(receipt); }}
                  sx={{
                    height: 20,
                    cursor: 'pointer',
                    boxShadow: 1,
                    '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' },
                    '& .MuiChip-icon': { fontSize: '0.875rem', ml: 0.5 }
                  }}
                />
              )}
              {/* AI-2: Duplicate chip — actionable (gated by receiptAI) */}
              {receipt.duplicateOf && (
                <LockedFeature feature="receiptAI" ctaPlan="premium" preview="inline" promoText="AI duplicate detection">
                  <Chip
                    size="small"
                    icon={<WarningAmberIcon />}
                    label="Duplicate"
                    color="warning"
                    variant="filled"
                    onClick={(e) => { e.stopPropagation(); onAction('viewDuplicate', receipt); }}
                    sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' }, '& .MuiChip-icon': { fontSize: '0.875rem', ml: 0.5 }, cursor: 'pointer', boxShadow: 1 }}
                  />
                </LockedFeature>
              )}
              {/* AI-4: Anomaly chip — actionable popover (gated by receiptAI) */}
              {receipt.anomaly?.flagged && (
                <LockedFeature feature="receiptAI" ctaPlan="premium" preview="inline" promoText="Spending anomaly detection">
                  <Chip
                    size="small"
                    icon={<ErrorOutlineIcon />}
                    label="Unusual"
                    color="error"
                    variant="filled"
                    onClick={(e) => { e.stopPropagation(); onAnomalyClick && onAnomalyClick(e.currentTarget, receipt.anomaly.reason); }}
                    sx={{ height: 22, cursor: 'pointer', boxShadow: 1, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' }, '& .MuiChip-icon': { fontSize: '0.875rem', ml: 0.5 } }}
                  />
                </LockedFeature>
              )}
              {/* AI-3: Re-analyse chip — maintenance, outlined (gated by receiptReanalyse) */}
              {(receipt.confidence < 60 || receipt.status === 'error') && (
                <LockedFeature feature="receiptReanalyse" ctaPlan="standard" preview="inline" promoText="Re-run OCR on this receipt">
                  <Chip
                    size="small"
                    icon={<RefreshIcon />}
                    label={t('receipts.reanalyse', 'Re-analyse')}
                    color="warning"
                    variant="outlined"
                    onClick={(e) => { e.stopPropagation(); onAction('reanalyse', receipt); }}
                    sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' }, cursor: 'pointer' }}
                  />
                </LockedFeature>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {formatRelativeTime(receipt.transactionDate || receipt.createdAt)}
            </Typography>
          </Box>

          {/* Amount */}
          <Typography 
            variant="h6" 
            fontWeight={700}
            sx={{ flexShrink: 0 }}
          >
            {formatCurrency(receipt.totalAmount || 0)}
          </Typography>

          {/* Actions Menu */}
          <IconButton 
            size={isMobile ? "medium" : "small"} 
            onClick={handleMenuOpen}
            aria-label="More options"
            sx={{ flexShrink: 0 }}
          >
            <MoreIcon />
          </IconButton>
        </Stack>
      </CardContent>

      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, pb: 2 } }}
        >
          <Box sx={{ width: 6, height: 6, borderRadius: 3, bgcolor: 'divider', mx: 'auto', my: 1.5 }} />
          <List>
            <ListItem button onClick={() => handleAction('view')}>
              <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t('receipts.viewDetails', 'View Details')}</ListItemText>
            </ListItem>
            <ListItem button onClick={() => handleAction('download')}>
              <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t('receipts.downloadReceipt', 'Download Receipt')}</ListItemText>
            </ListItem>
            <Divider />
            <ListItem button onClick={() => handleAction('delete')} sx={{ color: 'error.main' }}>
              <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>{t('common.delete', 'Delete')}</ListItemText>
            </ListItem>
          </List>
        </Drawer>
      ) : (
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleAction('view')}>
            <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('receipts.viewDetails', 'View Details')}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleAction('download')}>
            <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('receipts.downloadReceipt', 'Download Receipt')}</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => handleAction('delete')} sx={{ color: 'error.main' }}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>{t('common.delete', 'Delete')}</ListItemText>
          </MenuItem>
        </Menu>
      )}
    </Card>
  );
});

ReceiptCard.displayName = 'ReceiptCard';

// Main Component
const ReceiptsManagement = () => {
  const { currency } = useCurrency(); // eslint-disable-line no-unused-vars
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isCompact = useMediaQuery('(max-width:480px)');
  const { user } = useAuth(); // eslint-disable-line no-unused-vars
  const { check: checkUpgrade } = useUpgradeGate();

  // ── Core state ───────────────────────────────────────────────────────────────
  const [tabValue, setTabValue] = useState(0); // 0=All, 1=Linked, 2=Orphans
  const [dateFilter, setDateFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [showScanner, setShowScanner] = useState(false);
  const [processingReceipt, setProcessingReceipt] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Pagination ───────────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);

  // ── View / Delete ────────────────────────────────────────────────────────────
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // null | receiptId | 'bulk'
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ── Bulk select ──────────────────────────────────────────────────────────────
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // ── Upload dialog ────────────────────────────────────────────────────────────
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // ── Stats / KPI ──────────────────────────────────────────────────────────────
  const [stats, setStats] = useState(null);

  // ── Pull-to-refresh ──────────────────────────────────────────────────────────
  const [refreshing, setRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const listContainerRef = useRef(null);

  // ── AI feature state ──────────────────────────────────────────────────────────
  const [reanalysing, setReanalysing] = useState(null); // receiptId being reanalysed
  const [duplicateDialog, setDuplicateDialog] = useState(null); // { receipt, duplicateInfo }
  const [linkDrawer, setLinkDrawer] = useState({ open: false, receipt: null });
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  // Change 2: Anomaly popover (works on mobile — no tooltip hover needed)
  const [anomalyPopover, setAnomalyPopover] = useState({ anchor: null, reason: '' });

  // Change 4: poll interval ref — cleaned up on unmount
  const pollIntervalRef = useRef(null);

  // Change 5: filter drawer + active filter count
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // ── F8: compute date range from filter ──────────────────────────────────────
  const computeDateRange = useCallback((filter) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    switch (filter) {
      case 'today':
        return { from: today.toISOString(), to: new Date().toISOString() };
      case 'week': case '7d':
        return { from: new Date(today.getTime() - 7 * 86400000).toISOString(), to: new Date().toISOString() };
      case 'month': case '30d':
        return { from: new Date(today.getTime() - 30 * 86400000).toISOString(), to: new Date().toISOString() };
      case '90d':
        return { from: new Date(today.getTime() - 90 * 86400000).toISOString(), to: new Date().toISOString() };
      case '180d':
        return { from: new Date(today.getTime() - 180 * 86400000).toISOString(), to: new Date().toISOString() };
      case 'year':
        return { from: new Date(now.getFullYear(), 0, 1).toISOString(), to: new Date().toISOString() };
      default:
        return {};
    }
  }, []);

  // Change 5: badge count for active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (sourceFilter && sourceFilter !== 'all') count++;
    if (dateFilter && dateFilter !== 'all') count++;
    return count;
  }, [sourceFilter, dateFilter]);

  // ── F2: Core fetch function ──────────────────────────────────────────────────
  const fetchReceipts = useCallback(async (pageNum = 1, append = false) => {
    if (!append) setLoading(true);
    else setLoadingMore(true);
    setError(null);

    // F9: linked param from tab
    const linked = tabValue === 1 ? 'true' : tabValue === 2 ? 'false' : undefined;
    // F3: source param
    const source = sourceFilter !== 'all' ? sourceFilter : undefined;
    // F8: date range
    const dateRange = computeDateRange(dateFilter);

    try {
      const res = await api.get('/receipts', {
        params: {
          page: pageNum,
          limit: 20,
          search: debouncedSearch || undefined,
          source,
          linked,
          ...dateRange
        }
      });
      const data = res.data;
      const incoming = (data.receipts || []).map(r => ({ ...r, id: r._id || r.id }));
      const pagination = data.pagination || {};

      if (append) {
        setReceipts(prev => [...prev, ...incoming]);
      } else {
        setReceipts(incoming);
      }
      setHasMore((pagination.page || pageNum) < (pagination.totalPages || 1));
      setPage(pageNum);
    } catch (err) {
      setError(t('receipts.errorLoading', 'Failed to load receipts. Please try again.'));
    } finally {
      if (!append) setLoading(false);
      else setLoadingMore(false);
    }
  }, [tabValue, sourceFilter, dateFilter, debouncedSearch, computeDateRange, t]);

  // ── F7: Load stats silently ──────────────────────────────────────────────────
  useEffect(() => {
    api.get('/receipts/stats')
      .then(res => setStats(res.data?.data || null))
      .catch(() => {}); // silent fail if endpoint not deployed
  }, []);

  // ── Initial load + re-fetch when filters change ──────────────────────────────
  useEffect(() => {
    fetchReceipts(1, false);
  }, [fetchReceipts]); // fetchReceipts is memoized by its filter deps

  // ── F3: Debounce search query ────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ── F2: IntersectionObserver for infinite scroll ─────────────────────────────
  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !loadingMore && hasMore) {
        const nextPage = page + 1;
        fetchReceipts(nextPage, true);
      }
    }, { threshold: 0.1 });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, page, fetchReceipts]);

  // ── R23: Keyboard shortcuts ──────────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (deleteConfirm) setDeleteConfirm(null);
        else if (viewDialogOpen) { setViewDialogOpen(false); setViewingReceipt(null); }
        else if (showScanner) setShowScanner(false);
      }
      if (e.key === 'Enter' && deleteConfirm && deleteConfirm !== 'bulk') {
        e.preventDefault();
        handleDelete(deleteConfirm);
        setDeleteConfirm(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [deleteConfirm, viewDialogOpen, showScanner]); // eslint-disable-line react-hooks/exhaustive-deps

  // Change 4: cleanup poll interval on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  // ── R25: Pull-to-refresh ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!isMobile) return;
    const el = listContainerRef.current;
    if (!el) return;

    let startY = null;
    let startX = null;
    let currentPull = 0;
    let isRefreshing = false;

    const onTouchStart = (e) => {
      if ((window.scrollY || document.documentElement.scrollTop) <= 0 && !isRefreshing) {
        startY = e.touches[0].clientY;
        startX = e.touches[0].clientX;
      }
    };

    const onTouchMove = (e) => {
      if (startY === null || isRefreshing) return;
      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - startX);
      const dy = touch.clientY - startY;
      if (deltaX > 8) return; // horizontal swipe — don't trigger refresh
      if (dy > 0 && (window.scrollY || document.documentElement.scrollTop) <= 0) {
        currentPull = Math.min(dy, 120);
        setPullDistance(currentPull);
        if (dy > 10) e.preventDefault();
      } else {
        startY = null;
        startX = null;
        currentPull = 0;
        setPullDistance(0);
      }
    };

    const onTouchEnd = () => {
      if (currentPull > 60 && !isRefreshing) {
        isRefreshing = true;
        setRefreshing(true);
        fetchReceipts(1).finally(() => {
          setRefreshing(false);
          isRefreshing = false;
        });
      }
      currentPull = 0;
      setPullDistance(0);
      startY = null;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [isMobile, fetchReceipts]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── F10: Group receipts by month (useMemo, no standalone function) ────────────
  const groupedReceipts = useMemo(() => {
    const groups = {};
    receipts.forEach(receipt => {
      const date = new Date(receipt.transactionDate || receipt.createdAt);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!groups[monthKey]) {
        groups[monthKey] = {
          label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
          receipts: []
        };
      }
      groups[monthKey].receipts.push(receipt);
    });
    return Object.entries(groups)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([, value]) => value);
  }, [receipts]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(1);
  };

  const handleOpenScanner = () => setShowScanner(true);

  const handleReceiptCaptured = (receiptData) => {
    setShowScanner(false);
    setProcessingReceipt(receiptData);
  };

  const handleProcessingComplete = () => {
    setProcessingReceipt(null);
    fetchReceipts(1);
  };

  const handleProcessingError = () => {
    setProcessingReceipt(null);
  };

  // F1: use api instance
  const handleReceiptAction = async (action, receipt) => {
    if (action === 'view') {
      try {
        setLoading(true);
        const res = await api.get(`/receipts/${receipt.id}`);
        setViewingReceipt(res.data.receipt || res.data);
        setViewDialogOpen(true);
      } catch (err) {
        setError(t('receipts.errorLoadingDetails', 'Failed to load receipt details'));
      } finally {
        setLoading(false);
      }
    } else if (action === 'download') {
      const link = document.createElement('a');
      link.href = getImageUrl(receipt.id);
      link.setAttribute('download', `receipt-${receipt.id}.jpg`);
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setSnackbar({ open: true, message: t('receipts.downloaded', 'Receipt downloaded'), severity: 'success' });
    } else if (action === 'delete') {
      setDeleteConfirm(receipt.id);
    } else if (action === 'reanalyse') {
      try {
        setReanalysing(receipt.id);
        await api.post(`/receipts/${receipt.id}/process`);
        setSnackbar({ open: true, message: t('receipts.reanalyseStarted', 'Re-analysis started — checking for updates…'), severity: 'info' });

        // Poll for up to 30 seconds every 2.5 seconds
        let attempts = 0;
        const maxAttempts = 12;
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = setInterval(async () => {
          attempts++;
          try {
            const res = await api.get(`/receipts/${receipt.id}`);
            const updated = res.data?.receipt || res.data?.data || res.data;
            if (updated && updated.status !== 'processing') {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
              setReanalysing(null);
              setReceipts(prev => prev.map(r =>
                (r.id || r._id) === (receipt.id || receipt._id)
                  ? { ...r, ...updated, id: updated._id || updated.id }
                  : r
              ));
              setSnackbar({ open: true, message: `Re-analysis complete! Confidence: ${updated.confidence || 0}%`, severity: 'success' });
              return;
            }
          } catch { /* keep polling */ }

          if (attempts >= maxAttempts) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            setReanalysing(null);
            setSnackbar({ open: true, message: 'Re-analysis is taking longer than usual. Check back in a moment.', severity: 'warning' });
          }
        }, 2500);

      } catch {
        setReanalysing(null);
        setSnackbar({ open: true, message: 'Re-analysis failed to start', severity: 'error' });
      }
    } else if (action === 'viewDuplicate') {
      try {
        const res = await api.get(`/receipts/${receipt.id}/duplicate-check`);
        if (res.data.isDuplicate) {
          setDuplicateDialog({ receipt, duplicateInfo: res.data });
        } else {
          setSnackbar({ open: true, message: 'No duplicate found', severity: 'info' });
        }
      } catch {
        setSnackbar({ open: true, message: 'Could not load duplicate info', severity: 'error' });
      }
    }
  };

  // F1: use api instance
  const handleDelete = async (receiptId) => {
    try {
      await api.delete(`/receipts/${receiptId}`);
      setReceipts(prev => prev.filter(r => (r.id || r._id) !== receiptId));
      setSnackbar({ open: true, message: t('receipts.deleted', 'Receipt deleted'), severity: 'success' });
    } catch (err) {
      setError(t('receipts.errorDeleting', 'Failed to delete receipt'));
    }
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setViewingReceipt(null);
  };

  // AI-1 + AI-7: receipt was linked, optionally show suggested category
  const handleLinked = (receiptId, txId, suggestedCategory) => {
    setReceipts(prev => prev.map(r => r.id === receiptId ? { ...r, linkedTransactionId: txId } : r));
    const msg = suggestedCategory
      ? `${t('receipts.linkSuccess', 'Receipt linked to transaction!')} ${t('receipts.suggestedCategory', 'Suggested category')}: ${suggestedCategory}`
      : t('receipts.linkSuccess', 'Receipt linked to transaction!');
    setSnackbar({ open: true, message: msg, severity: 'success', duration: suggestedCategory ? 6000 : 4000 });
  };

  // ── F4: Select helpers ────────────────────────────────────────────────────────
  const handleToggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    setBulkLoading(true);
    const count = selectedIds.size;
    try {
      await api.patch('/receipts/bulk', { ids: [...selectedIds], action: 'delete' });
      setReceipts(prev => prev.filter(r => !selectedIds.has(r.id || r._id)));
      setSelectedIds(new Set());
      setSelectMode(false);
      setSnackbar({ open: true, message: `${count} receipt(s) deleted`, severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Bulk delete failed', severity: 'error' });
    } finally {
      setBulkLoading(false);
      setDeleteConfirm(null);
    }
  };

  const handleBulkUnlink = async () => {
    setBulkLoading(true);
    const count = selectedIds.size;
    try {
      await api.patch('/receipts/bulk', { ids: [...selectedIds], action: 'unlink' });
      setSelectedIds(new Set());
      setSelectMode(false);
      setSnackbar({ open: true, message: `${count} receipt(s) unlinked`, severity: 'success' });
      fetchReceipts(1);
    } catch {
      setSnackbar({ open: true, message: 'Bulk unlink failed', severity: 'error' });
    } finally {
      setBulkLoading(false);
    }
  };

  // ── F5: Upload files ─────────────────────────────────────────────────────────
  const handleUploadFiles = async () => {
    setUploading(true);
    let uploaded = 0;
    for (const file of uploadFiles) {
      try {
        const formData = new FormData();
        formData.append('receipt', file);
        formData.append('source', 'upload');
        const res = await api.post('/receipts/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          params: { source: 'upload' }
        });
        if (res.data?.receiptId) {
          uploaded++;
          if (uploaded === 1) {
            setProcessingReceipt({ receiptId: res.data.receiptId, source: 'upload' });
          }
        }
      } catch (e) {
        console.error('Upload failed for', file.name, e);
        // Soft paywall on OCR upload — surface modal (interceptor may also fire)
        const soft = extractSoftPaywall(e) || e.softPaywall;
        if (soft || isSoftPaywallError(e)) {
          if (soft) dispatchSoftPaywall(soft);
          break; // stop batch; user can stay on free
        }
      }
    }
    setUploading(false);
    setUploadDialogOpen(false);
    setUploadFiles([]);
    setSnackbar({ open: true, message: `${uploaded} receipt(s) queued for processing`, severity: 'success' });
    setTimeout(() => fetchReceipts(1), 2000);
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Stack 
        direction="row" 
        alignItems="center" 
        justifyContent="space-between" 
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant={isCompact ? "h5" : "h4"} fontWeight={700}>
            {t('receipts.title', 'Receipts')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('receipts.count', '{{count}} receipts', { count: receipts.length })}
          </Typography>
        </Box>
        
        <Stack direction="row" spacing={1} alignItems="center">
          {/* F4: Select toggle */}
          <Button
            size="small"
            variant={selectMode ? 'contained' : 'outlined'}
            onClick={() => { setSelectMode(!selectMode); setSelectedIds(new Set()); }}
          >
            {selectMode ? 'Cancel' : 'Select'}
          </Button>

          {/* F5: Upload button */}
          {!isMobile && (
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => setUploadDialogOpen(true)}
              size={isCompact ? "medium" : "large"}
            >
              {t('receipts.upload', 'Upload')}
            </Button>
          )}

          {!isMobile && (
            <Button
              variant="contained"
              startIcon={<CameraIcon />}
              onClick={handleOpenScanner}
              size={isCompact ? "medium" : "large"}
            >
              {t('receipts.scanReceipt', 'Scan Receipt')}
            </Button>
          )}
        </Stack>
      </Stack>

      {/* F7: KPI strip */}
      {stats && (
        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
          {[
            { label: 'Total', value: stats.totalReceipts, icon: '🧾' },
            { label: 'Linked', value: `${Math.round(stats.linkedRate || 0)}%`, icon: '🔗' },
            { label: 'Orphans', value: stats.orphanCount, icon: '⚠️' },
          ].map(({ label, value, icon }) => (
            <Paper key={label} sx={{ flex: 1, p: 1.5, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="h5" fontWeight={700}>{icon} {value}</Typography>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Paper>
          ))}
        </Stack>
      )}

      {/* F3: Search bar + Filters button */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          placeholder={t('receipts.searchPlaceholder', 'Search merchant or notes...')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ flex: 1 }}
        />
        <Badge badgeContent={activeFilterCount} color="primary" invisible={activeFilterCount === 0}>
          <IconButton
            onClick={() => setFilterDrawerOpen(true)}
            sx={{
              border: 1,
              borderColor: activeFilterCount > 0 ? 'primary.main' : 'divider',
              borderRadius: 2,
              color: activeFilterCount > 0 ? 'primary.main' : 'text.secondary',
              bgcolor: activeFilterCount > 0 ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
            }}
          >
            <TuneIcon />
          </IconButton>
        </Badge>
      </Stack>

      {/* F9: Tabs — linkage axis */}
      <Tabs 
        value={tabValue} 
        onChange={handleTabChange}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          label="All"
          sx={{ minWidth: isCompact ? 60 : 100 }}
        />
        <Tab 
          label={
            <Stack direction="row" spacing={0.5} alignItems="center">
              <LinkIcon fontSize="small" />
              <span>{t('receipts.linked', 'Linked')}</span>
              {stats?.linkedCount > 0 && (
                <Chip size="small" label={stats.linkedCount} sx={{ height: 18, ml: 0.5 }} />
              )}
            </Stack>
          }
          sx={{ minWidth: isCompact ? 80 : 120 }}
        />
        <Tab 
          label={
            <Stack direction="row" spacing={0.5} alignItems="center">
              <LinkOffIcon fontSize="small" />
              <span>{t('receipts.orphans', 'Orphans')}</span>
              {stats?.orphanCount > 0 && (
                <Chip size="small" label={stats.orphanCount} sx={{ height: 18, ml: 0.5 }} />
              )}
            </Stack>
          }
          sx={{ minWidth: isCompact ? 80 : 120 }}
        />
      </Tabs>

      {/* F4: Bulk action bar */}
      {selectMode && selectedIds.size > 0 && (
        <Paper sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
          <Typography sx={{ flex: 1 }}>{selectedIds.size} selected</Typography>
          <LockedFeature feature="receiptAI" ctaPlan="premium" preview="badge" promoText="AI expense reports">
            <Button size="small" color="inherit" variant="outlined" onClick={() => setReportDialogOpen(true)} disabled={selectedIds.size < 2}>
              📋 {t('receipts.report', 'Report')}
            </Button>
          </LockedFeature>
          <Button size="small" color="inherit" variant="outlined" onClick={handleBulkUnlink} disabled={bulkLoading}>Unlink</Button>
          <Button size="small" color="error" variant="contained" sx={{ bgcolor: 'error.dark' }} onClick={() => setDeleteConfirm('bulk')} disabled={bulkLoading}>Delete</Button>
        </Paper>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Stack spacing={1.5}>
          {[1, 2, 3].map(i => (
            <Paper key={i} sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Skeleton variant="rounded" width={56} height={56} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width="60%" height={24} />
                <Skeleton width="40%" height={16} sx={{ mt: 0.5 }} />
              </Box>
              <Skeleton width={60} height={28} />
            </Paper>
          ))}
        </Stack>
      ) : receipts.length === 0 ? (
        // Empty State — differentiated per context
        <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
          <ReceiptIcon sx={{ fontSize: 80, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchQuery
              ? `No receipts match "${searchQuery}"`
              : tabValue === 1
              ? t('receipts.noLinkedReceipts', 'No linked receipts yet')
              : tabValue === 2
              ? t('receipts.noOrphanReceipts', "You're all caught up! No unlinked receipts.")
              : t('receipts.noReceipts', 'No receipts yet')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {searchQuery
              ? 'Try a different search term or clear filters.'
              : tabValue === 1
              ? t('receipts.noLinkedReceiptsDesc', 'Link a receipt to a transaction to track your spending!')
              : tabValue === 2
              ? (activeFilterCount > 0 ? 'No orphan receipts match your current filters.' : '')
              : t('receipts.noReceiptsDesc', 'Start scanning receipts to track your expenses')}
          </Typography>
          {searchQuery ? (
            <Button variant="outlined" onClick={() => setSearchQuery('')} sx={{ mt: 2, borderRadius: 3 }}>
              Clear Search
            </Button>
          ) : tabValue === 0 && (
            <Button
              variant="contained"
              size="large"
              startIcon={<CameraIcon />}
              onClick={handleOpenScanner}
              sx={{ mt: 2, borderRadius: 3, px: 4, py: 1.5 }}
            >
              {t('receipts.scanYourFirst', 'Scan Your First Receipt')}
            </Button>
          )}
        </Box>
      ) : (
        // Receipt List (Grouped by Month)
        <Box ref={listContainerRef}>
          {/* R25: Pull-to-refresh indicator */}
          {isMobile && (pullDistance > 0 || refreshing) && (
            <Box sx={{ textAlign: 'center', py: 1, transition: 'opacity 0.2s', opacity: pullDistance > 30 || refreshing ? 1 : pullDistance / 30 }}>
              {refreshing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="primary">Refreshing...</Typography>
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {pullDistance > 60 ? '↑ Release to refresh' : '↓ Pull down to refresh'}
                </Typography>
              )}
            </Box>
          )}

          {groupedReceipts.map((group, idx) => (
            <Box key={idx} sx={{ mb: 4 }}>
              {/* Month Header */}
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mb: 1.5,
                  fontWeight: 600,
                  position: 'sticky',
                  top: 0,
                  bgcolor: 'background.default',
                  zIndex: 1,
                  py: 1
                }}
              >
                {group.label}
              </Typography>

              {/* Receipts */}
              {group.receipts.map(receipt => (
                <ReceiptCard
                  key={receipt.id || receipt._id}
                  receipt={receipt}
                  onAction={handleReceiptAction}
                  t={t}
                  selectMode={selectMode}
                  isSelected={selectedIds.has(receipt.id || receipt._id)}
                  onSelect={handleToggleSelect}
                  onLinkClick={(r) => {
                    // Smart link uses AI suggestions → premium gate
                    if (!checkUpgrade('receiptAI', 'premium', { promoText: 'AI smart linking' })) return;
                    setLinkDrawer({ open: true, receipt: r });
                  }}
                  onAnomalyClick={(anchor, reason) => setAnomalyPopover({ anchor, reason })}
                />
              ))}
            </Box>
          ))}

          {/* F2: Infinite scroll sentinel */}
          <Box ref={sentinelRef} sx={{ py: 1, textAlign: 'center' }}>
            {loadingMore && <CircularProgress size={24} />}
            {!hasMore && receipts.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {t('receipts.allLoaded', 'All receipts loaded')}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Floating Action Button (Mobile) */}
      {isMobile && (
        <Fab
          color="primary"
          onClick={handleOpenScanner}
          sx={{ position: 'fixed', bottom: 80, right: 16, zIndex: 1000 }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Receipt Scanner Modal */}
      {showScanner && (
        <ReceiptCapture
          open={showScanner}
          onCancel={() => setShowScanner(false)}
          onReceiptCaptured={handleReceiptCaptured}
        />
      )}

      {/* Receipt Processing Screen */}
      {processingReceipt && (
        <ReceiptProcessingScreen
          receiptData={processingReceipt}
          onComplete={handleProcessingComplete}
          onError={handleProcessingError}
        />
      )}

      {/* F5: Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Receipts</DialogTitle>
        <DialogContent>
          <Box
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
              setUploadFiles(prev => [...prev, ...files].slice(0, 10));
            }}
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.04) }
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography>Drag & drop receipt images here</Typography>
            <Typography variant="caption" color="text.secondary">or click to browse • JPG, PNG • max 10 files</Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => {
                const files = Array.from(e.target.files).slice(0, 10);
                setUploadFiles(prev => [...prev, ...files].slice(0, 10));
              }}
            />
          </Box>
          {uploadFiles.length > 0 && (
            <Stack spacing={0.5} sx={{ mt: 2 }}>
              {uploadFiles.map((f, i) => (
                <Chip
                  key={i}
                  label={f.name}
                  onDelete={() => setUploadFiles(prev => prev.filter((_, j) => j !== i))}
                />
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadDialogOpen(false); setUploadFiles([]); }}>Cancel</Button>
          <Button variant="contained" disabled={uploadFiles.length === 0 || uploading} onClick={handleUploadFiles}>
            {uploading ? <CircularProgress size={20} /> : `Upload ${uploadFiles.length > 0 ? uploadFiles.length : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI-5: Receipt Detail Dialog (replaces inline dialog) */}
      <ReceiptDetailDialog
        open={viewDialogOpen}
        receipt={viewingReceipt}
        onClose={handleCloseViewDialog}
        onReanalyse={(id) => handleReceiptAction('reanalyse', { id })}
      />

      {/* Delete / Bulk Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>
          {deleteConfirm === 'bulk'
            ? `Delete ${selectedIds.size} receipts?`
            : t('receipts.deleteTitle', 'Delete Receipt?')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('receipts.deleteWarning', 'This action cannot be undone.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>{t('common.cancel', 'Cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (deleteConfirm === 'bulk') {
                handleBulkDelete();
              } else {
                handleDelete(deleteConfirm);
                setDeleteConfirm(null);
              }
            }}
          >
            {t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI-2: Duplicate Comparison Dialog */}
      <Dialog open={!!duplicateDialog} onClose={() => setDuplicateDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>⚠ {t('receipts.duplicateTitle', 'Possible Duplicate Receipt')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This receipt looks very similar to one you already have ({duplicateDialog?.duplicateInfo?.duplicateConfidence || 0}% match).
          </Typography>
          {duplicateDialog && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Paper variant="outlined" sx={{ flex: 1, p: 2 }}>
                <Typography variant="caption" color="primary" fontWeight={700}>THIS RECEIPT</Typography>
                <Typography variant="body2" fontWeight={600}>{duplicateDialog.receipt.merchantName}</Typography>
                <Typography variant="body2">{formatCurrency(duplicateDialog.receipt.totalAmount)}</Typography>
                <Typography variant="caption" color="text.secondary">{formatDate(duplicateDialog.receipt.transactionDate)}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ flex: 1, p: 2, bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                <Typography variant="caption" color="warning.main" fontWeight={700}>EXISTING RECEIPT</Typography>
                <Typography variant="body2" fontWeight={600}>{duplicateDialog.duplicateInfo?.duplicate?.merchantName}</Typography>
                <Typography variant="body2">{formatCurrency(duplicateDialog.duplicateInfo?.duplicate?.totalAmount)}</Typography>
                <Typography variant="caption" color="text.secondary">{formatDate(duplicateDialog.duplicateInfo?.duplicate?.transactionDate)}</Typography>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateDialog(null)}>{t('receipts.keepBoth', 'Keep Both')}</Button>
          <Button color="error" variant="contained" onClick={() => {
            if (duplicateDialog) handleDelete(duplicateDialog.receipt.id);
            setDuplicateDialog(null);
          }}>Delete This One</Button>
        </DialogActions>
      </Dialog>

      {/* AI-1: Smart Link Suggestions drawer */}
      <ReceiptLinkSuggestions
        open={linkDrawer.open}
        receipt={linkDrawer.receipt}
        onClose={() => setLinkDrawer({ open: false, receipt: null })}
        onLinked={handleLinked}
      />

      {/* AI-6: Expense Report Dialog */}
      <ExpenseReportDialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        selectedIds={selectedIds}
        receipts={receipts}
      />

      {/* Change 2: Anomaly Popover — works on mobile (no hover needed) */}
      <Popover
        open={Boolean(anomalyPopover.anchor)}
        anchorEl={anomalyPopover.anchor}
        onClose={() => setAnomalyPopover({ anchor: null, reason: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 1.5, maxWidth: 280, borderRadius: 2 } }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <ErrorOutlineIcon color="error" fontSize="small" sx={{ mt: 0.2, flexShrink: 0 }} />
          <Box>
            <Typography variant="subtitle2" color="error">Unusual Spend</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {anomalyPopover.reason || 'This amount is higher than your usual spend at this merchant.'}
            </Typography>
          </Box>
        </Stack>
      </Popover>

      {/* Change 5: Filter Drawer — bottom sheet on mobile */}
      <Drawer
        anchor="bottom"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16 } }}
      >
        <Box sx={{ p: 2, pb: 3 }}>
          {/* Drag handle */}
          <Box sx={{ width: 40, height: 4, bgcolor: 'divider', borderRadius: 2, mx: 'auto', mb: 2 }} />

          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Filters</Typography>

          {/* Source filter */}
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>Source</Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
            {['all', 'scan', 'email', 'upload', 'manual'].map((s) => (
              <Chip
                key={s}
                label={s === 'all' ? 'All Sources' : s.charAt(0).toUpperCase() + s.slice(1)}
                onClick={() => { setSourceFilter(s); setPage(1); }}
                variant={sourceFilter === s ? 'filled' : 'outlined'}
                color={sourceFilter === s ? 'primary' : 'default'}
              />
            ))}
          </Stack>

          {/* Date filter */}
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>Date Range</Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
            {[
              { value: 'all', label: 'All Time' },
              { value: '7d', label: 'Last 7 days' },
              { value: '30d', label: 'Last 30 days' },
              { value: '90d', label: 'Last 3 months' },
              { value: '180d', label: 'Last 6 months' },
            ].map((d) => (
              <Chip
                key={d.value}
                label={d.label}
                onClick={() => { setDateFilter(d.value); setPage(1); }}
                variant={dateFilter === d.value ? 'filled' : 'outlined'}
                color={dateFilter === d.value ? 'primary' : 'default'}
              />
            ))}
          </Stack>

          {/* Clear + Apply */}
          <Stack direction="row" spacing={2}>
            <Button fullWidth variant="outlined" onClick={() => { setSourceFilter('all'); setDateFilter('all'); setPage(1); }} disabled={activeFilterCount === 0}>
              Clear Filters
            </Button>
            <Button fullWidth variant="contained" onClick={() => setFilterDrawerOpen(false)}>
              Show Results
            </Button>
          </Stack>
        </Box>
      </Drawer>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.duration || 4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ReceiptsManagement;
